# Route: /tracking/forms
# Manage tracking forms (create, view, approve, deprecate)
# Permission: TrackingForm:read

page-tracking-form-list:
  type: Page
  archetype: list
  meta:
    title: $t.tracking.forms.meta_title
    route: /tracking/forms
    layout: shell
    auth: required
  data:
    templates:
      model: TrackingForm[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - form-list-header
    - form-list-table

# ── Header ───────────────────────────────────────────────────────────────────

form-list-header:
  type: PageHeader
  content: $t.tracking.forms.title
  actions:
    - label: $t.tracking.forms.btn_create
      route: /tracking/forms/new/edit
      color: info
  hide_actions_on_sm: true

# ── Table ────────────────────────────────────────────────────────────────────

form-list-table:
  type: Table
  bind:
    rows: templates
  empty: form-list-empty
  # M1 list-filter adoption (DEC-090) — opt-in client-side quick-filter: same
  # shape as the asset class/instance lists. Case-insensitive substring across
  # the visible columns (incl. versionStatus) over the loaded rows — the single
  # filter affordance for this list.
  filter:
    fields: [displayName, name, versionStatus, applicableContexts]
    placeholder: $t.common.quick_filter
  columns:
    - key: displayName
      label: $t.tracking.forms.col_display_name
      tokens:
        font_size: $font.size.sm
    - key: name
      priority: medium
      label: $t.tracking.forms.col_name
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: versionNumber
      label: $t.tracking.forms.col_version
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    # F92 M-3 — color-coded badge, matching reason-vocabulary-list's chain
    # exactly (same five-state model, same Obsolete=caution per M-2) so the
    # two sibling reference-data lists render versionStatus identically
    # rather than one plain-text and one badge.
    - key: versionStatus
      label: $t.tracking.forms.col_status
      display: badge
      variant:
        when: "$row.versionStatus == 'Effective'"
        value: go
        else:
          when: "$row.versionStatus == 'Approved'"
          value: info
          else:
            when: "$row.versionStatus == 'Obsolete'"
            value: caution
            else: info
    - key: applicableContexts
      priority: low
      label: $t.tracking.forms.col_contexts
      tokens:
        font_size: $font.size.sm
    # F92 — count of structured fields this version declares. Same
    # dotted-column-key approach as reason-vocabulary-list's entries.length
    # (no dedicated "count" format exists in XP85; this is the existing
    # supported dotted-key mechanism, not a new construct).
    - key: fields.length
      label: $t.tracking.forms.col_fields_count
      tokens:
        font_size: $font.size.sm
  row_action:
    type: navigate
    route: /tracking/forms/:id
    params:
      id: $row.id
  a11y:
    label: $t.tracking.forms.table_label
  tokens:
    border: $border.default
    radius: $radius.md
    background: $color_scheme.light.color.surface-elevated

form-list-empty:
  type: EmptyState
  content: $t.tracking.forms.empty
  body: $t.tracking.forms.empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.lg
